import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { downloadReport, fetchReports } from '@/api/endpoints';
import { describe } from '@/lib/errors';

const PRESETS = [1, 7, 30, 90, 365];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * CSV exports of the system's records, for review and audit.
 *
 * Every export is itself written to the audit log with its date range, and
 * the file name says what it holds and for which dates.
 */
export function ReportsPage() {
  const catalogue = useQuery({ queryKey: ['reports'], queryFn: fetchReports });
  const [reportKey, setReportKey] = useState('');
  const [mode, setMode] = useState<'days' | 'dates'>('days');
  const [days, setDays] = useState(30);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const reports = catalogue.data?.reports ?? [];
  const chosen = reports.find((report) => report.key === reportKey);

  async function exportReport() {
    if (!reportKey) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const { blob, filename } = await downloadReport(
        reportKey,
        mode === 'days' ? { days } : { from, to },
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setDone(filename);
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Reports</h1>
        <p>
          Export the system&apos;s records as CSV for review and audit. Times are UTC, amounts are
          exact, and every row carries its record id. Each export is itself recorded in the audit
          trail.
        </p>
      </div>

      <section className="panel">
        <div className="panel-body" style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {error ? <div className="notice notice--error">{error}</div> : null}
          {done ? <div className="notice">Downloaded {done}.</div> : null}

          <div className="assign">
            <label>
              <span>Report</span>
              <select value={reportKey} onChange={(e) => setReportKey(e.target.value)}>
                <option value="">choose…</option>
                {reports.map((report) => (
                  <option key={report.key} value={report.key}>
                    {report.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Range</span>
              <select value={mode} onChange={(e) => setMode(e.target.value as 'days' | 'dates')}>
                <option value="days">last N days</option>
                <option value="dates">between dates</option>
              </select>
            </label>
            {mode === 'days' ? (
              <label>
                <span>Days</span>
                <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                  {PRESETS.map((value) => (
                    <option key={value} value={value}>
                      {value === 1 ? 'last 24 hours' : `last ${value} days`}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <>
                <label>
                  <span>From</span>
                  <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
                </label>
                <label>
                  <span>To (inclusive)</span>
                  <input type="date" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)} />
                </label>
              </>
            )}
            <button type="button" disabled={!reportKey || busy} onClick={() => void exportReport()}>
              {busy ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>

          {chosen ? (
            <p style={{ color: 'var(--text-secondary)' }}>{chosen.description}</p>
          ) : null}
          {catalogue.data ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
              One export covers at most {catalogue.data.max_days} days.
            </p>
          ) : null}
        </div>
      </section>
    </>
  );
}
